import { Controller } from "@hotwired/stimulus"

// Formats a text input with thousands separators while the user types.
//
// Usage:
//   input data-controller="number-formatter"
//
// Behavior:
// - Strips any character that isn't a digit, decimal point, or leading minus.
// - Allows a single decimal point.
// - Re-formats the integer part with commas after every keystroke.
// - Preserves the caret position relative to the digits the user has typed.
// - On submit (the form's "submit" event) the commas are stripped so the
//   server receives a plain numeric string. The Document model also strips
//   commas in its sum= setter as a belt-and-braces measure.
export default class extends Controller {
  connect() {
    this.format(this.element)
    this.boundFormat = this.onInput.bind(this)
    this.boundStrip = this.onSubmit.bind(this)
    this.element.addEventListener("input", this.boundFormat)
    if (this.element.form) {
      this.element.form.addEventListener("submit", this.boundStrip)
    }
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundFormat)
    if (this.element.form) {
      this.element.form.removeEventListener("submit", this.boundStrip)
    }
  }

  onInput(event) {
    this.format(event.target)
  }

  onSubmit() {
    this.element.value = this.stripCommas(this.element.value)
  }

  format(input) {
    const original = input.value
    const caretFromEnd = original.length - (input.selectionStart ?? original.length)

    // Preserve a leading minus sign, then keep digits and at most one decimal point.
    const negative = original.trimStart().startsWith("-")
    let cleaned = original.replace(/[^\d.]/g, "")
    const firstDot = cleaned.indexOf(".")
    if (firstDot !== -1) {
      cleaned = cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, "")
    }

    const [intPart, decPart] = cleaned.split(".")
    const intFormatted = intPart ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ""
    const base = decPart !== undefined ? `${intFormatted}.${decPart}` : intFormatted
    const formatted = negative ? `-${base}` : base

    if (formatted !== original) {
      input.value = formatted
      const newPos = Math.max(0, formatted.length - caretFromEnd)
      input.setSelectionRange(newPos, newPos)
    }
  }

  stripCommas(value) {
    return (value || "").replace(/,/g, "")
  }
}
